import type { Schedule, TimeOff } from "@/features/calendar/types";
import { isTimeInSchedule } from "@/lib/timezone-utils";
import { useMemo } from "react";

export interface TimeSlotAvailability {
  isClosed: boolean; // True if outside schedule hours
  affectedMembers: string[]; // Members with time-off during this slot
}

/**
 * Hook to determine schedule and time-off status for calendar time slots
 */
export function useScheduleAvailability(
  schedules: Schedule[],
  timeOffs: TimeOff[],
  selectedMembers: string[],
) {
  // Get the default schedule
  const defaultSchedule = useMemo(
    () => schedules.find((s) => s.is_default),
    [schedules],
  );

  // Parse schedule rules once
  const scheduleRules = useMemo(() => {
    if (!defaultSchedule) return [];

    try {
      const rules = defaultSchedule.rrules.split("\n").filter((r) => r.trim());
      const weeklyRules = rules.filter((r) => r.startsWith("RRULE:"));

      return weeklyRules
        .map((rule) => {
          // Parse rule: RRULE:FREQ=WEEKLY;BYDAY=MO,TU;BYHOUR=9;BYMINUTE=0;DURATION=480
          const match = rule.match(
            /^RRULE:FREQ=WEEKLY;BYDAY=([A-Z,]+);BYHOUR=(\d{1,2});BYMINUTE=(\d{1,2});DURATION=(\d+)$/,
          );

          if (!match) return null;

          const [, days, startHour, startMin, durationMin] = match;

          return {
            days: days.split(","),
            startHour: parseInt(startHour),
            startMin: parseInt(startMin),
            durationMin: parseInt(durationMin),
          };
        })
        .filter(
          (
            r,
          ): r is {
            days: string[];
            startHour: number;
            startMin: number;
            durationMin: number;
          } => r !== null,
        );
    } catch (error) {
      console.warn("Error parsing schedule RRULE:", error);
      return [];
    }
  }, [defaultSchedule]);

  /**
   * Check if a time slot is within working hours based on the default schedule
   * Properly handles timezone conversion between local time and schedule timezone
   */
  const isTimeSlotOpen = useMemo(
    () => (date: Date, hour: number, minute: number) => {
      if (!defaultSchedule || scheduleRules.length === 0) return true;

      return isTimeInSchedule(
        date,
        hour,
        minute,
        defaultSchedule.timezone,
        scheduleRules,
      );
    },
    [defaultSchedule, scheduleRules],
  );

  /**
   * Get members who have time-off during a specific time slot
   */
  const getMembersWithTimeOff = useMemo(
    () => (date: Date, hour: number, minute: number) => {
      const slotDate = new Date(date);
      slotDate.setHours(hour, minute, 0, 0);
      const slotTime = slotDate.getTime();

      const affectedMembers: string[] = [];

      for (const timeOff of timeOffs) {
        const startTime = new Date(timeOff.start_date).getTime();
        const endTime = new Date(timeOff.end_date).getTime();

        // Check if slot is within time-off period
        if (slotTime >= startTime && slotTime < endTime) {
          // Check affected members
          if (
            timeOff.affected_members &&
            timeOff.affected_members.id &&
            timeOff.affected_members.id.length > 0
          ) {
            // Add members if they are selected
            for (const memberId of timeOff.affected_members.id) {
              if (
                selectedMembers.length === 0 ||
                selectedMembers.includes(memberId)
              ) {
                affectedMembers.push(memberId);
              }
            }
          } else {
            // No specific members means it affects everyone
            return ["*"];
          }
        }
      }

      return affectedMembers;
    },
    [timeOffs, selectedMembers],
  );

  /**
   * Get availability status for a specific time slot
   */
  const getSlotAvailability = useMemo(
    () =>
      (date: Date, hour: number, minute: number): TimeSlotAvailability => {
        const isClosed = !isTimeSlotOpen(date, hour, minute);
        const affectedMembers = getMembersWithTimeOff(date, hour, minute);

        return {
          isClosed,
          affectedMembers,
        };
      },
    [isTimeSlotOpen, getMembersWithTimeOff],
  );

  return {
    defaultSchedule,
    getSlotAvailability,
  };
}
